// Measurement-driven compaction for the POS V2 create/edit cart.
//
// Replaces the estimate-based getCartCompactionTier decision (kept only as
// the synchronous first-paint seed) with a stepwise state machine fed by
// real rendered heights: it escalates a tier when the lines list drops
// below a usable minimum and de-escalates only when the cached heights of
// the collapsed blocks prove they fit back with slack to spare. This makes
// the collapse content-aware (long custom-field titles, extra totals rows,
// future footer blocks) instead of cliff-based on window height.
//
// Convergence: the evaluation watch sources are raw measured sizes and
// content signatures — never anything derived from the tier — so a tier
// write can never re-trigger evaluation by itself. Because ResizeObserver
// refs can be mutually incoherent for one tick (separate observers deliver
// with microtask checkpoints between them, and vueuse zeroes a ref
// synchronously when its element unmounts), decisions never read the
// observer refs directly: evaluate() snapshots every height from the DOM
// (offsetHeight forces layout), so any trigger timing yields a coherent
// picture. Volatile banners can therefore never flip tiers.
//
// Keyboard-aware rules (the on-screen keyboard resizes the layout viewport
// since the interactive-widget=resizes-content / native-resizer fix):
// - Window-height inputs (isTiny, the first-paint seed) read the stable
//   window size (use-stable-window-size.ts), which ignores keyboard-driven
//   height shrink — tiers key off device size, not the IME-shrunk viewport.
// - Focus guard: a transition that would unmount the region containing
//   document.activeElement (getTierTransitionUnmounts) is deferred; the
//   watch on the active element re-evaluates once focus leaves. Benign
//   staleness: if a focused tier-2 popover textarea is unmounted without a
//   blur event (Escape closes the popover), no watch source changes and a
//   deferred transition stays unapplied until the next focus change, resize
//   or measurement tick — self-healing, because the guard always reads the
//   live DOM.
import { computed, ComputedRef, reactive, watch } from 'vue';
import {
  MaybeComputedElementRef,
  unrefElement,
  useActiveElement,
  useElementSize,
  useTimeoutFn,
  useWindowSize,
} from '@vueuse/core';
import ICustomField from '@/interfaces/ICustomField';
import type { IPosOrderTotals } from '@/domain/pos/pos-types';
import {
  COMMENT_ROW_DELTA,
  EST_COMMENT_1ROW,
  EST_COMMENT_2ROWS,
  EST_FIELDS_BASE,
  EST_FIELDS_PER_FIELD,
  EST_TOTALS_BASE,
  EST_TOTALS_PER_EXTRA_ROW,
  getBlockHeight,
  getCartCompactionTier,
  getTierTransitionUnmounts,
  ICartBlockMeasure,
  isCartTiny,
  resolveCartTierStep,
  TCartTier,
  TTierUnmountRegion,
} from '@/domain/pos/pos-compaction';
import {
  RESYNC_DELAY_MS,
  useStableWindowSize,
} from '@/composition/use-stable-window-size';

// .cart-footer flex gap: tw-gap-3 (12px) at tier 0, tw-gap-2 (8px) compact.
const FOOTER_GAP_FULL = 12;
const FOOTER_GAP_COMPACT = 8;

const COMMENT_SIGNATURE = 'comment';

const MEASURE_OPTIONS = { box: 'border-box' as const };

interface IUseCartCompactionOptions {
  /** Inline instances only — never the popover-hosted copies. */
  linesRef: MaybeComputedElementRef;
  fieldsRef: MaybeComputedElementRef;
  commentRef: MaybeComputedElementRef;
  totalsRef: MaybeComputedElementRef;
  chipsRowRef: MaybeComputedElementRef;
  errorRef: MaybeComputedElementRef;
  conflictRef: MaybeComputedElementRef;
  activeFields: ComputedRef<ICustomField[]>;
  totals: ComputedRef<IPosOrderTotals | null>;
}

interface IUseCartCompactionReturn {
  tier: ComputedRef<TCartTier>;
  isTiny: ComputedRef<boolean>;
  /** Rows for the inline comment textarea (the tier-2 popover copy keeps 3). */
  commentRows: ComputedRef<1 | 2>;
}

interface IBlockCache {
  fields: ICartBlockMeasure | null;
  comment1: ICartBlockMeasure | null;
  comment2: ICartBlockMeasure | null;
  totals: ICartBlockMeasure | null;
}

export function useCartCompaction(
  opts: IUseCartCompactionOptions
): IUseCartCompactionReturn {
  // IME-blind: keyboard-driven height shrink never reaches the tier logic.
  const { height: stableHeight } = useStableWindowSize();
  // Raw height, only compared against stableHeight to detect the transient
  // "keyboard still open / just closing" window (raw < stable).
  const { height: rawHeight } = useWindowSize();
  const activeElement = useActiveElement();

  /**
   * Live border-box height straight from the DOM. Forces a synchronous
   * layout, so every read within one evaluate() call is coherent — unlike
   * the ResizeObserver refs, which can lag each other by a tick.
   */
  function measureHeight(target: MaybeComputedElementRef): number {
    const el = unrefElement(target);
    return el instanceof HTMLElement ? el.offsetHeight : 0;
  }

  /** Live cart column width (lines container border-box). */
  function measureColumnWidth(): number {
    const el = unrefElement(opts.linesRef);
    return el instanceof HTMLElement ? el.offsetWidth : 0;
  }

  const linesSize = useElementSize(opts.linesRef, undefined, MEASURE_OPTIONS);
  const fieldsSize = useElementSize(opts.fieldsRef, undefined, MEASURE_OPTIONS);
  const commentSize = useElementSize(
    opts.commentRef,
    undefined,
    MEASURE_OPTIONS
  );
  const totalsSize = useElementSize(opts.totalsRef, undefined, MEASURE_OPTIONS);
  const errorSize = useElementSize(opts.errorRef, undefined, MEASURE_OPTIONS);
  const conflictSize = useElementSize(
    opts.conflictRef,
    undefined,
    MEASURE_OPTIONS
  );

  const isTiny = computed(() => isCartTiny(stableHeight.value));

  const seedTier = computed(() =>
    getCartCompactionTier(stableHeight.value, opts.activeFields.value.length)
  );

  // Owned by the state machine after the first real measurement; follows
  // the seed until then (never falls back to the seed afterwards).
  const state = reactive<{ machineTier: TCartTier; everMeasured: boolean }>({
    machineTier: seedTier.value,
    everMeasured: false,
  });

  // Direct write without the focus guard is safe here: nothing can be
  // focused before the cart has ever rendered a measurable lines list.
  watch(seedTier, (value) => {
    if (!state.everMeasured) state.machineTier = value;
  });

  // No isTiny bypass here: the tiny pin is applied inside evaluate(), so
  // every tier change — including the pin — passes the focus guard.
  const tier = computed<TCartTier>(() => state.machineTier);

  const commentRows = computed<1 | 2>(() => (tier.value >= 1 ? 1 : 2));

  //#region Block height cache

  const cache: IBlockCache = {
    fields: null,
    comment1: null,
    comment2: null,
    totals: null,
  };

  const fieldsSignature = computed(() =>
    opts.activeFields.value.map((f) => `${f.id}:${f.title}`).join('|')
  );

  // Tuple of which conditional rows are visible (see PosV2CartTotals.vue).
  const totalsSignature = computed(() => {
    const t = opts.totals.value;
    return [
      (t?.delivery || 0) > 0,
      (t?.productsTax || 0) > 0,
      (t?.discountedAmount || 0) > 0,
      (t?.roundDifference || 0) !== 0,
    ].join(',');
  });

  const totalsEstimate = computed(() => {
    const extraRows = totalsSignature.value
      .split(',')
      .filter((v) => v === 'true').length;
    return EST_TOTALS_BASE + extraRows * EST_TOTALS_PER_EXTRA_ROW;
  });

  // Cache writes trigger on the block's own measured height ONLY: by the
  // time a ResizeObserver tick reports it, the signature/rows computeds are
  // already up to date, so the pairing is always correct. Including the
  // signatures as sources would briefly write stale heights under new tags.
  watch(fieldsSize.height, (height) => {
    if (height > 0) {
      cache.fields = {
        height,
        width: measureColumnWidth(),
        signature: fieldsSignature.value,
      };
    }
  });

  watch(commentSize.height, (height) => {
    if (height > 0) {
      const slot = commentRows.value === 1 ? 'comment1' : 'comment2';
      cache[slot] = {
        height,
        width: measureColumnWidth(),
        signature: COMMENT_SIGNATURE,
      };
    }
  });

  watch(totalsSize.height, (height) => {
    if (height > 0) {
      cache.totals = {
        height,
        width: measureColumnWidth(),
        signature: totalsSignature.value,
      };
    }
  });

  /**
   * Comment height for a given row count: cached variant first, then the
   * sibling variant shifted by COMMENT_ROW_DELTA, then the estimate
   * (EST_COMMENT_2ROWS === EST_COMMENT_1ROW + COMMENT_ROW_DELTA keeps the
   * chain consistent).
   */
  function getCommentHeight(rows: 1 | 2, width: number): number {
    if (rows === 1) {
      return getBlockHeight(
        cache.comment1,
        width,
        COMMENT_SIGNATURE,
        getBlockHeight(
          cache.comment2,
          width,
          COMMENT_SIGNATURE,
          EST_COMMENT_2ROWS
        ) - COMMENT_ROW_DELTA
      );
    }
    return getBlockHeight(
      cache.comment2,
      width,
      COMMENT_SIGNATURE,
      getBlockHeight(
        cache.comment1,
        width,
        COMMENT_SIGNATURE,
        EST_COMMENT_1ROW
      ) + COMMENT_ROW_DELTA
    );
  }

  //#endregion

  //#region Evaluation

  /**
   * Lines area with the volatile blocks (validation error, edit-conflict
   * banner) added back: their appearance/disappearance must never flip
   * tiers, so they are excluded from the budget. All heights come from
   * live DOM reads for a coherent snapshot.
   */
  function computeStableLinesArea(linesHeight: number): number {
    const gap = state.machineTier >= 1 ? FOOTER_GAP_COMPACT : FOOTER_GAP_FULL;
    const errorHeight = measureHeight(opts.errorRef);
    const conflictHeight = measureHeight(opts.conflictRef);
    let area = linesHeight;
    if (errorHeight > 0) area += errorHeight + gap;
    if (conflictHeight > 0) area += conflictHeight + gap;
    return area;
  }

  /** Extra height the next-richer tier needs; null at tier 0. */
  function computeNextBlocksDelta(): number | null {
    const currentTier = state.machineTier;
    if (currentTier === 0) return null;

    const width = measureColumnWidth();

    if (currentTier === 2) {
      // Tier 1 restores the inline fields + 1-row comment (chips row stays).
      const fieldCount = opts.activeFields.value.length;
      const gap = FOOTER_GAP_COMPACT;
      if (fieldCount === 0) {
        return getCommentHeight(1, width) + gap;
      }
      const fields = getBlockHeight(
        cache.fields,
        width,
        fieldsSignature.value,
        EST_FIELDS_BASE + fieldCount * EST_FIELDS_PER_FIELD
      );
      return fields + getCommentHeight(1, width) + 2 * gap;
    }

    // Tier 0 swaps the chips row for the full totals and grows the comment
    // to 2 rows. Gap-size differences are absorbed by the slack.
    const totals = getBlockHeight(
      cache.totals,
      width,
      totalsSignature.value,
      totalsEstimate.value
    );
    // Rendered at tiers 1-2; measured live because its observer may not
    // have reported yet right after a tier transition.
    const chipsRow = measureHeight(opts.chipsRowRef);
    return (
      totals - chipsRow + (getCommentHeight(2, width) - getCommentHeight(1, width))
    );
  }

  /**
   * Whether document.activeElement sits inside the region a transition
   * would unmount. Live DOM reads (consistent with evaluate()'s snapshot
   * discipline) — never the possibly-stale useActiveElement ref.
   */
  function focusWithinRegion(
    region: Exclude<TTierUnmountRegion, null>
  ): boolean {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement)) return false;
    const containers =
      region === 'inline-editables'
        ? [opts.fieldsRef, opts.commentRef]
        : [opts.chipsRowRef];
    return containers.some((container) => {
      const el = unrefElement(container);
      return el instanceof HTMLElement && el.contains(active);
    });
  }

  function evaluate() {
    const linesEl = unrefElement(opts.linesRef);
    // Hidden (mobile tab switch: display:none ancestor nulls offsetParent)
    // is NOT the same as visible-but-squeezed-to-0: a focused-deferred
    // freeze can let the footer squeeze the lines list to 0px, and the
    // post-blur re-evaluation must still see that as "escalate", not as a
    // hidden pane to freeze on.
    const linesHidden =
      !(linesEl instanceof HTMLElement) || linesEl.offsetParent === null;
    const linesHeight = linesHidden ? 0 : linesEl.offsetHeight;

    if (!state.everMeasured) {
      // Keep the seed until the cart reports a real size (no flash).
      if (linesHeight <= 0) return;
      state.everMeasured = true;
    } else if (linesHidden) {
      // Pane hidden (mobile tab switch): freeze, never re-seed.
      return;
    }

    // Tiny guardrail owns the layout (120px lines floor, pane scrolls);
    // the pin to 2 goes through the focus guard like every other change.
    const next: TCartTier = isTiny.value
      ? 2
      : resolveCartTierStep({
          tier: state.machineTier,
          stableLinesArea: computeStableLinesArea(linesHeight),
          nextBlocksDelta: computeNextBlocksDelta(),
        });

    const region = getTierTransitionUnmounts(state.machineTier, next);
    if (region !== null && focusWithinRegion(region)) {
      // Never unmount the focused element (it would dismiss the keyboard);
      // the activeElement watch re-evaluates once focus leaves the region.
      return;
    }
    state.machineTier = next;
  }

  // Triggers (tick discipline): raw measured sizes plus the content
  // signatures — none derived from the tier, so a tier write never
  // re-triggers evaluation. The signatures matter when a collapsed block's
  // content changes without any rendered size changing (e.g. switching to
  // a draft type without fields while at tier 2 on a static viewport):
  // the delta shrinks but no ResizeObserver tick would ever come.
  // isTiny (stable height) replaces the removed raw-height tier bypass so
  // tiny-boundary crossings still trigger a step.
  // Decisions never read these refs — evaluate() snapshots the DOM.
  watch(
    [
      linesSize.height,
      errorSize.height,
      conflictSize.height,
      fieldsSignature,
      totalsSignature,
      isTiny,
    ],
    evaluate,
    { immediate: true }
  );

  // Applies deferred transitions once focus leaves the protected region.
  // Timing subtlety: on mobile, blur fires 150–250 ms BEFORE the
  // keyboard-close resize. Evaluating immediately would apply the deferred
  // escalation on the still-squeezed DOM — a visible chips flash, and (via
  // the de-escalation slack) a possible one-way tier ratchet. While the raw
  // height sits below the stable height (keyboard closing, or a desktop
  // shrink not yet resynced), the re-evaluation waits out the same settle
  // window the stable-size resync uses; the close resize itself triggers an
  // immediate, correctly-measured evaluate() through the lines-size watch.
  const { start: startSettledEvaluate, stop: cancelSettledEvaluate } =
    useTimeoutFn(evaluate, RESYNC_DELAY_MS, { immediate: false });

  watch(activeElement, () => {
    if (rawHeight.value < stableHeight.value) {
      startSettledEvaluate();
      return;
    }
    cancelSettledEvaluate();
    evaluate();
  });

  //#endregion

  return { tier, isTiny, commentRows };
}
